import { ReactNode, useLayoutEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useEnsureWorkflowVersionTracesSearchContext } from "./hooks/useEnsureWorkflowVersionTracesSearchContext";
import { useWorkflowVersionTracesDateRangeConfig } from "./hooks/useWorkflowVersionTracesDateRangeConfig";
import { buildWorkflowVersionLockedFilters } from "./utils/buildWorkflowVersionLockedFilters";

import { StructuredSearchProvider, useStructuredSearchStore } from "@/components/StructuredSearch";
import type { Filter } from "@/components/StructuredSearch/config/types";
import {
  WORKFLOW_ANALYTICS_URL_NAMESPACE,
  readNamespacedParam,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { useSyncStructuredSearchConstraints } from "@/components/StructuredSearch/hooks/useSyncStructuredSearchConstraints";
import { tracesSearchSchema } from "@/components/StructuredSearch/schemas/traces-search-schema";
import {
  createStructuredSearchDefaultDateRange,
  type CompleteDateRange,
} from "@/components/StructuredSearch/stores/structured-search/structured-search-store";

interface WorkflowVersionTracesSearchProviderProps {
  children: ReactNode;
  workflowId: string;
  workflowName: string;
  versionNumber: string;
  versionCreatedAt?: string;
}

const isDefaultStructuredSearchDateRange = (from: Date, to: Date): boolean => {
  const defaultRange = createStructuredSearchDefaultDateRange();
  return (
    Math.abs(from.getTime() - defaultRange.from.getTime()) < 60_000 &&
    Math.abs(to.getTime() - defaultRange.to.getTime()) < 60_000
  );
};

const WorkflowVersionTracesSearchSync = ({
  lockedFilters,
  dateRange,
  searchContextReady,
}: {
  lockedFilters: Filter[];
  dateRange?: CompleteDateRange;
  searchContextReady: boolean;
}) => {
  const store = useStructuredSearchStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSeededRef = useRef(false);
  const startParam = readNamespacedParam(
    searchParams,
    WORKFLOW_ANALYTICS_URL_NAMESPACE,
    "startTime",
  );
  const endParam = readNamespacedParam(searchParams, WORKFLOW_ANALYTICS_URL_NAMESPACE, "endTime");
  const startTime = startParam ? new Date(startParam) : null;
  const endTime = endParam ? new Date(endParam) : null;
  const urlHasCustomDateRange =
    !!startTime && !!endTime && !isDefaultStructuredSearchDateRange(startTime, endTime);

  useLayoutEffect(() => {
    if (!dateRange || hasSeededRef.current || urlHasCustomDateRange) {
      return;
    }

    hasSeededRef.current = true;
    store.setDateRange(dateRange);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(WORKFLOW_ANALYTICS_URL_NAMESPACE.startTime, dateRange.from.toISOString());
        next.set(WORKFLOW_ANALYTICS_URL_NAMESPACE.endTime, dateRange.to.toISOString());
        return next;
      },
      { replace: true },
    );
  }, [dateRange, setSearchParams, store, urlHasCustomDateRange]);

  useLayoutEffect(() => {
    // Block URL sync / grid fetch until the version date is seeded and locked
    // workflow filters are available (avoids unfiltered workspace-wide searches).
    store.setUrlHydrationReady(
      searchContextReady &&
        lockedFilters.length > 0 &&
        (urlHasCustomDateRange || hasSeededRef.current || !dateRange),
    );
  }, [dateRange, lockedFilters.length, searchContextReady, store, urlHasCustomDateRange]);

  useSyncStructuredSearchConstraints({
    lockedFilters: lockedFilters.length > 0 ? lockedFilters : undefined,
    syncDateRange: false,
    store,
  });

  return null;
};

const WorkflowVersionTracesSearchProvider = ({
  children,
  workflowId,
  workflowName,
  versionNumber,
  versionCreatedAt,
}: WorkflowVersionTracesSearchProviderProps) => {
  const workflowVersionNumber = Number(versionNumber);
  const hasValidVersionNumber = Number.isFinite(workflowVersionNumber);

  const lockedWorkflowFilters = useMemo(
    () =>
      buildWorkflowVersionLockedFilters({
        workflowId,
        workflowName,
        workflowVersionNumber: hasValidVersionNumber ? workflowVersionNumber : undefined,
      }),
    [hasValidVersionNumber, workflowId, workflowName, workflowVersionNumber],
  );

  const versionDateRangeConfig = useWorkflowVersionTracesDateRangeConfig({
    workflowVersion: versionCreatedAt ? { created_at: versionCreatedAt } : undefined,
  });

  const searchContextReady = useEnsureWorkflowVersionTracesSearchContext(workflowId, versionNumber);

  const providerKey = `${workflowId}:${versionNumber}`;

  return (
    <StructuredSearchProvider key={providerKey} config={tracesSearchSchema}>
      <WorkflowVersionTracesSearchSync
        lockedFilters={lockedWorkflowFilters}
        dateRange={versionDateRangeConfig?.initialDateRange}
        searchContextReady={searchContextReady}
      />
      {children}
    </StructuredSearchProvider>
  );
};

export default WorkflowVersionTracesSearchProvider;
